import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Typography } from '@mui/material'
import { blueGrey } from '@mui/material/colors'
import MenuItem from '@mui/material/MenuItem'
import { Box } from '@mui/system'
import PropTypes from 'prop-types'

import { useGetOrderQuery } from '@/app/services/orders'
import { selectOrganisation, selectOrganisationId, selectPaidFeatures } from '@/app/slices/organisationSlice'
import { TbxTooltip } from '@/common/components'
import FormInputDate from '@/common/components/FormComponents/FormInputDate'
import FormInputSelect from '@/common/components/FormComponents/FormInputSelect'
import FormInputText from '@/common/components/FormComponents/FormInputText'
import TbxLocalizationProvider from '@/common/components/TbxLocalizationProvider/TbxLocalizationProvider'
import { useToolBoxTreatments } from '@/common/hooks/useToolBoxTreatments'
import { DELIVERY_PRICING_METHODS, ORDER_PAYMENT_STATUS, ORDER_STATUS_ARRAY } from '@/common/utils/Constants/Constants'

const classes = {
    shippingInputs: {
        width: 'calc(100% / 4)',
    },

    disabledInput: {
        '& .Mui-disabled': {
            textFillColor: '#34495E',
        },
        '& .MuiInput-input.Mui-disabled': {
            fontWeight: 'bold',
        },
    },
    divider: {
        borderStyle: 'dashed',
        borderColor: blueGrey[200],
    },

    disabledMenuItem: {
        '& .MuiMenuItem-root.Mui-disabled': {
            pointerEvents: 'initial',
        },
    },
}

const OrderCheckoutShippingOptions = ({
    customerDeliveryProviderOption,
    defaultDeliveryProvider,
    deliveryPriceMethod,
    form,
    shippingOptions,
}) => {
    const { t } = useTranslation()
    const { orderId } = useParams()

    const { showShipping } = useToolBoxTreatments()

    const { control, setValue, watch } = form

    const organisationId = useSelector(selectOrganisationId)
    const storeSettings = useSelector(selectOrganisation)

    const { data: order } = useGetOrderQuery({
        organisationId,
        orderId,
    })

    const selectedShippingOption = watch('shippingOption')
    const deliveryProvider = watch('customerDeliveryProvider')

    const isOrdered = [ORDER_STATUS_ARRAY.Ordered].includes(order?.status)

    const isInvoiced = [ORDER_STATUS_ARRAY.Invoiced].includes(order?.status)

    const isPending = [ORDER_STATUS_ARRAY.PendingOrderConfirmation].includes(order?.status)

    const isPaid = isOrdered && order?.paymentStatus !== ORDER_PAYMENT_STATUS.Unpaid

    const isDisabled = [
        ORDER_STATUS_ARRAY.Lost,
        ORDER_STATUS_ARRAY.Cancelled,
        ORDER_STATUS_ARRAY.Rejected,
        ORDER_STATUS_ARRAY.Voided,
    ].includes(order?.status)

    const isReadOnly = isInvoiced || isPending || isPaid

    const handleDeliveryProviderBlur = (e) => {
        const { value } = e.target
        if (value === defaultDeliveryProvider) {
            setValue('deliveryProviderToUse', 'defaultProvider', { shouldValidate: true })
            setValue('customerDeliveryAccountNumber', '', { shouldValidate: true })
        }
        if (value !== defaultDeliveryProvider) {
            setValue('deliveryProviderToUse', 'customerProvider', { shouldValidate: true })
        }

        if (!value) {
            setValue('customerDeliveryProvider', defaultDeliveryProvider, { shouldValidate: true })
            setValue('deliveryProviderToUse', 'defaultProvider', { shouldValidate: true })
            setValue('customerDeliveryAccountNumber', '', { shouldValidate: false })
        }
    }

    return (
        <TbxLocalizationProvider>
            <Box
                sx={{
                    alignItems: 'flex-start',
                    alignSelf: 'stretch',
                    display: 'flex',
                    flexWrap: 'nowrap',
                    gap: 3,
                    justifyContent: 'flex-start',
                }}
            >
                <TbxTooltip
                    title={
                        isReadOnly
                            ? t(
                                  'Shipping option cannot be changed after the order is paid or invoiced or if is pending order confirmation'
                              )
                            : ''
                    }
                    arrow
                >
                    <FormInputSelect
                        control={control}
                        isDisabled={isDisabled || shippingOptions.length === 1}
                        isReadOnly={isReadOnly}
                        label={t('Shipping option')}
                        name="shippingOption"
                        rules={undefined}
                        size="small"
                        sx={[classes.shippingInputs, classes.disabledInput]}
                    >
                        {shippingOptions.map((option) => (
                            <MenuItem
                                key={option.value}
                                value={option.value}
                            >
                                {t(option.label)}
                            </MenuItem>
                        ))}
                    </FormInputSelect>
                </TbxTooltip>

                {showShipping &&
                storeSettings?.webStoreDeliveryEnabled &&
                !!storeSettings?.webStoreDeliveryRegions.length &&
                selectedShippingOption === 'Delivery' &&
                deliveryPriceMethod === DELIVERY_PRICING_METHODS['PayUponDelivery'].value ? (
                    <>
                        <FormInputText
                            color="secondary"
                            control={control}
                            disabled={isDisabled || !customerDeliveryProviderOption}
                            inputProps={{
                                'data-testid': 'order-customer-delivery-provider-input',
                            }}
                            label={t('Delivery provider')}
                            name="customerDeliveryProvider"
                            rules={{ required: 'This field is required' }}
                            size="small"
                            sx={[classes.disabledInput, classes.shippingInputs]}
                            onHandleBlur={handleDeliveryProviderBlur}
                        />

                        {customerDeliveryProviderOption ? (
                            <FormInputText
                                color="secondary"
                                control={control}
                                disabled={isDisabled}
                                inputProps={{
                                    'data-testid': 'order-customer-delivery-account-number-input',
                                }}
                                label={t('Delivery account number')}
                                name="customerDeliveryAccountNumber"
                                placeholder={t('N/A')}
                                rules={{
                                    required: {
                                        value: deliveryProvider !== defaultDeliveryProvider,
                                        message: 'This field is required',
                                    },
                                }}
                                size="small"
                                sx={[classes.disabledInput, classes.shippingInputs]}
                            />
                        ) : null}
                    </>
                ) : null}

                <FormInputDate
                    control={control}
                    disabled={isDisabled}
                    inputProps={{
                        'data-testid': 'order-require-shipping-date-input',
                    }}
                    label={
                        selectedShippingOption === 'Delivery'
                            ? t('Requested delivery date')
                            : t('Requested pickup date')
                    }
                    name="requiredShippingDate"
                    placeholder={t('Select a date')}
                    size="small"
                    sx={[classes.disabledInput, classes.shippingInputs]}
                />
            </Box>
            {customerDeliveryProviderOption ? (
                <Typography
                    color="text.secondary"
                    sx={{ mt: 1 }}
                    variant="small"
                >
                    {t('Optionally nominate your own delivery provider and account number.')}
                </Typography>
            ) : null}
        </TbxLocalizationProvider>
    )
}

OrderCheckoutShippingOptions.propTypes = {
    form: PropTypes.object.isRequired,
    shippingOptions: PropTypes.array.isRequired,
    customerDeliveryProviderOption: PropTypes.bool,
    defaultDeliveryProvider: PropTypes.string,
    deliveryPriceMethod: PropTypes.string,
}

export default OrderCheckoutShippingOptions
